import type { UIComponent } from '../schemas/base';

/**
 * Streaming chunk format from backend
 */
interface StreamingChunk {
    delta?: { content: string };
}

/**
 * Final response format from backend
 */
interface FinalResponse {
    id: string;
    object: string;
    data: {
        text: string;
        components: UIComponent[];
    };
}

/**
 * Full response format (non-streaming)
 */
export interface GenUIResponse {
    text: string;
    components: UIComponent[];
}

/**
 * Parse a streaming chunk
 */
export function parseStreamingChunk(line: string): StreamingChunk | null {
    if (!line.startsWith('data: ')) return null;

    const data = line.slice(6).trim();
    if (data === '[DONE]') return null;

    try {
        return JSON.parse(data) as StreamingChunk;
    } catch {
        return null;
    }
}

/**
 * Check if chunk is final response (has 'data' field with components)
 */
export function isFinalResponse(chunk: unknown): chunk is FinalResponse {
    return (
        typeof chunk === 'object' &&
        chunk !== null &&
        'data' in chunk &&
        typeof (chunk as FinalResponse).data === 'object'
    );
}

/**
 * Extract text delta from chunk
 */
export function extractTextDelta(chunk: StreamingChunk): string | null {
    return chunk.delta?.content ?? null;
}

/**
 * Parse final response to extract components
 */
export function parseFinalResponse(data: string): FinalResponse | null {
    try {
        const parsed = JSON.parse(data.replace('data: ', '').trim());
        if (isFinalResponse(parsed)) {
            return parsed;
        }
        return null;
    } catch {
        return null;
    }
}

/**
 * Validate component type
 */
export function isValidComponentType(type: unknown): type is UIComponent['type'] {
    return type === 'table' || type === 'chart' || type === 'card';
}

/**
 * Parse and validate a component
 */
export function parseComponent(data: unknown): UIComponent | null {
    if (typeof data !== 'object' || data === null) return null;

    const obj = data as Record<string, unknown>;
    if (!isValidComponentType(obj.type)) return null;

    // Basic validation passed, return as UIComponent
    // More detailed validation happens at render time
    return data as UIComponent;
}
